import React, { useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import { Formik } from "formik"
import * as Yup from "yup"
import { makeStyles } from "@mui/styles"
import moment from "moment"
import dayjs from "dayjs"
import { uniqBy } from "lodash"

import { COLORS } from "../../utils"
import ProductionStepsFilterForm from "./ProductionStepsFilterForm"
import {
	getProductionStepsSiteSelector,
	getProductionStepsSitesSelector,
	getProductionStepsKitchenAreaSelector,
	getProductionStepsProductionDaySelector,
	isPSEGenerationDialogOpen,
	isGenerationForOneRecipe, isPSEFusionDialogOpen, isPSEFusionStep2DialogOpen,
	getPSEgenerationForANewAddedRecipeSelector
} from "../../reducers/Production/ProductionSteps"
import {
	updateProductionStepsFilters,
	showProductionStepsSupervision,
	generateProductionStepExecutions,
	doPSEsExist,
	mergeProductionStepExecutions, updatePSEMergeHistory,
	generatePSEsForANewAddedRecipe
} from "../../actions/ProductionSteps/ProductionStepExecutions"
import { confirm } from "../../components/react-await-dialog/react-await-dialog"
import DateFormDialog from "../../components/DateFormDialog"
import PSEGenerationForOneRecipeDialog from "./PSEGenerationForOneRecipeDialog"
import { openAppMessageSnackBar } from "../../actions/Utils/app"
import PSEFusionStep2Dialog from "./PSEFusionStep2Dialog"
import {
	getPSEsByProductionDateToMerge
} from "../../parseManager/productionStepExecutions/parseProductionStepExecutionManager"
import {
	getReusableProductionStepsByStepIdsForMerge
} from "../../parseManager/recipe/parseReusableProductionStepsManager"
import { PSE_GENERATION_TYPE } from "../../utils/productionStepExecution"
import { PRODUCTION_STEP_EXECUTION_FILTER_STORAGE } from "../../utils/constant"
import { getReusableStepsAndProductionStepsForPSEsMerge } from "../../utils/reusableStepsUtils"

const schema = Yup.object().shape({
	site: Yup.string().required("Veuillez sélectionner un site"),
	kitchenArea: Yup.string(),
	productionDay: Yup.string().required("Veuillez sélectionner un jour de production")
})

const useStyles = makeStyles({
	wrapper: {
		display: "flex",
		flexDirection: "column",
		flexGrow: 1
	},
	container: {
		flexGrow: 1,
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
		backgroundColor: COLORS.GREY_SUPPLIER_BACKGROUND
	},
	error: {
		margin: "16px 0px"
	},
	pseCreationConfirm: {
		marginTop: 450,
		width: 500
	}
})

const ProductionStepsFilter = () => {
	const classes = useStyles()

	const dispatch = useDispatch()
	const sites = useSelector(getProductionStepsSitesSelector)
	const selectedSite = useSelector(getProductionStepsSiteSelector)
	const selectedKitchenArea = useSelector(getProductionStepsKitchenAreaSelector)
	const selectedProductionDay = useSelector(getProductionStepsProductionDaySelector)
	const [kitchenAreas, setKitchenAreas] = useState([])
	const openPSEGenerationDialog = useSelector(isPSEGenerationDialogOpen)
	const openPSEFusionDialog = useSelector(isPSEFusionDialogOpen)
	const openPSEFusionStep2Dialog = useSelector(isPSEFusionStep2DialogOpen)
	const openGenerationForOneRecipe = useSelector(isGenerationForOneRecipe)
	const openPSEGenerationForNewAddedRecipe = useSelector(getPSEgenerationForANewAddedRecipeSelector)
	const [selectedDate, setSelectedDate] = useState(moment.utc().add(1, "days").valueOf())
	const [reusableStepOptions, setReusableStepOptions] = useState([])
	const [reusableStepsForMerge, setReusableStepsForMerge] = useState([])
	const [reusableStepsByBaseMap, setReusableStepsByBaseMap] = useState(new Map())
	const [stepsIdsByReusableStepUsedAsBaseMap, setStepsIdsByReusableStepUsedAsBaseMap] = useState(new Map())

	const dailyPseItem = PRODUCTION_STEP_EXECUTION_FILTER_STORAGE + moment.utc().format("YYYY-MM-DD")
	for (let i = 0; i < localStorage.length; i++) {
		const key = localStorage.key(i)
		if (key.startsWith(PRODUCTION_STEP_EXECUTION_FILTER_STORAGE) && key !== dailyPseItem)
			localStorage.removeItem(key)
	}
	const dailyPseFilter = JSON.parse(localStorage.getItem(dailyPseItem)) || {}

	useEffect(() => {
		const updatedKitchenAreas = sites.reduce((acc, site) => {
			return [...acc, ...site.kitchenAreas]
		}, [])
		setKitchenAreas(updatedKitchenAreas)
	}, [sites])

	const handleClosePSEGenerationDialog = () => {
		dispatch({
			type: "TOGGLE_PSE_GENERATION_DIALOG",
			open: false
		})
	}

	const handleClosePSEFusionDialog = () => {
		dispatch({
			type: "TOGGLE_PSE_FUSION_DIALOG",
			open: false
		})
	}

	const handleClosePSEGenerationForNewAddedRecipeDialog = () => {
		dispatch({
			type: "TOGGLE_PSE_GENERATION_FOR_NEW_ADDED_RECIPE",
			open: false
		})
	}

	const togglePSEFusionDialog = async (values = null) => {
		// 1. Get all PSE ids for the selected date
		const productionStepIds = await getPSEsByProductionDateToMerge(values.date)
		let reusableSteps = []
		if (productionStepIds?.length > 1) {
			// 2. Get all reusable steps containing at least 2 of these production steps
			const { reusableProductionSteps, stepsByBaseMap, productionStepsIdByBaseSourceReusableStepMap } = await getReusableProductionStepsByStepIdsForMerge(productionStepIds)
			reusableSteps = reusableProductionSteps
			// Store the PSEs grouped by their baseReusableProductionStep
			// to be able to add all of them when one is selected
			setReusableStepsByBaseMap(stepsByBaseMap)
			// Store the production step ids for reusable steps used as base for other reusable steps
			setStepsIdsByReusableStepUsedAsBaseMap(productionStepsIdByBaseSourceReusableStepMap)
		}

		setSelectedDate(values.date)
		// steps with the same base should be counted once (only in the form selection list)
		// NOTE: when saved in the db later, all steps sharing the same base are saved
		const uniqueOptions = uniqBy(reusableSteps, "optionKey")
		setReusableStepOptions(uniqueOptions)
		setReusableStepsForMerge(reusableSteps)
		
		dispatch({
			type: "TOGGLE_PSE_FUSION_DIALOG",
			open: true,
			step2: values && values.date
		})
	}

	const handleGeneratePSE = async (values) => {
		const pseExists = await doPSEsExist(values.date)

		if (pseExists) {
			const response = await confirm({
				message: "Les étapes pour ce jour de production ont déjà été générées. Confirmez-vous la régénération ? Toute modification sera perdue",
				confirmText: "Confirmer",
				classes: {
					paper: classes.pseCreationConfirm
				}
			})

			if (response) {
				handleClosePSEGenerationDialog()
				dispatch(generateProductionStepExecutions({ productionDate: values.date, isNew: false }))
			}

			handleClosePSEGenerationDialog()
		} else {
			handleClosePSEGenerationDialog()
			dispatch(generateProductionStepExecutions({ productionDate: values.date }))
		}
	}

	const handleRegeneratePSEsForOneRecipe = async (values) => {
		const pseExists = await doPSEsExist(values.date, values.recipe && values.recipe.objectId)
		if (pseExists) {
			const response = await confirm({
				message: "Les étapes pour ce jour de production et cette recette ont déjà été générées. Confirmez-vous la régénération ? Toute modification sera perdue",
				confirmText: "Confirmer"
			})

			if (response) {
				handleClosePSEGenerationDialog()
				const recipeLabel = values.recipe.uniqueCode + " - " + values.recipe.name
				dispatch(generateProductionStepExecutions({ productionDate: values.date, recipeLabel, recipeId: values.recipe.objectId, isNew: false }))
			}

			handleClosePSEGenerationDialog()
		} else {
			dispatch(openAppMessageSnackBar("Aucune PSE trouvée", "error"))
			handleClosePSEGenerationDialog()
		}
	}

	const handleGeneratePSEsForANewRecipe = async (values) => {
		dispatch(generatePSEsForANewAddedRecipe({ productionDate: values.date, recipeId: values.recipe.objectId }))
		handleClosePSEGenerationForNewAddedRecipeDialog()
	}

	const handleMergeReusableSteps = async (values) => {
		const date = values.date

		const { selectedReusableStepsIds, productionStepsIds } = getReusableStepsAndProductionStepsForPSEsMerge({
			selectedReusableStepsIds: values.selectedReusableStepsIds,
			reusableStepsForMerge,
			reusableStepsByBaseMap
		})
		const allProductionStepIdsToMerge = [...productionStepsIds, ...Array.from(stepsIdsByReusableStepUsedAsBaseMap.values()).flat()]
		const uniqProductionStepsIds = uniqBy(allProductionStepIdsToMerge)

		await dispatch(mergeProductionStepExecutions(dayjs(date).utc().startOf("day").valueOf(), uniqProductionStepsIds))

		const allReusableStepsIds = [...selectedReusableStepsIds, ...Array.from(stepsIdsByReusableStepUsedAsBaseMap.keys())]
		const uniqReusableStepsIds = uniqBy(allReusableStepsIds)
		await dispatch(updatePSEMergeHistory(uniqReusableStepsIds, date))
		handleClosePSEGenerationDialog()
	}

	const _submitForm = (values) => {
		const site = sites.find(site => site.objectId === values.site)
		const kitchenArea = kitchenAreas.find(kitchenArea => kitchenArea.objectId === values.kitchenArea)
		const productionDay = values.productionDay
		// save filters into local storage
		dispatch(updateProductionStepsFilters(site, kitchenArea, productionDay))
		// redirection
		dispatch(showProductionStepsSupervision())
	}

	return (
		<div className={classes.wrapper}>
			<div className={classes.container}>
				<Formik
					validationSchema={schema}
					initialValues={{
						site: dailyPseFilter.site || selectedSite?.objectId || "",
						kitchenArea: dailyPseFilter.kitchenArea || selectedKitchenArea?.objectId || "",
						productionDay: dailyPseFilter.productionDay || moment.utc(selectedProductionDay).valueOf(),
					}}
					onSubmit={_submitForm}
				>
					{({
						values,
						handleChange,
						setFieldValue,
						handleSubmit,
					}) => {
						useEffect(() => {
							localStorage.setItem(dailyPseItem, JSON.stringify(values))
						}, [values])

						return (
							<ProductionStepsFilterForm
								sites={sites}
								values={values}
								setFieldValue={setFieldValue}
								handleChange={handleChange}
								handleSubmit={handleSubmit}
							/>
						)
					}}
				</Formik>
			</div>

			<DateFormDialog
				title="Veuillez sélectionner le jour de production pour lequel vous souhaitez générer les étapes de production."
				onClose={handleClosePSEGenerationDialog}
				open={openPSEGenerationDialog && !openGenerationForOneRecipe}
				onConfirm={handleGeneratePSE}
				buttonText="Générer les étapes de production"
				inputLabel="Jour de production"
			/>

			{/* Regnerate existing PSEs for a selected recipe */}
			<PSEGenerationForOneRecipeDialog
				title="Veuillez sélectionner le jour et la recette pour laquelle vous souhaitez régénérer les étapes de production"
				onClose={handleClosePSEGenerationDialog}
				open={openGenerationForOneRecipe && openPSEGenerationDialog}
				onConfirm={handleRegeneratePSEsForOneRecipe}
				buttonText="Regénérer les étapes de production"
				inputLabel="Jour de production"
				type={PSE_GENERATION_TYPE.REGENERATION}
			/>

			{/* Generate non existing PSEs for a selected recipe */}
			<PSEGenerationForOneRecipeDialog
				title="Veuillez sélectionner le jour et la recette pour laquelle vous souhaitez ajouter les étapes de production"
				onClose={handleClosePSEGenerationForNewAddedRecipeDialog}
				open={openPSEGenerationForNewAddedRecipe}
				onConfirm={handleGeneratePSEsForANewRecipe}
				buttonText="Valider"
				inputLabel="Jour de production"
				type={PSE_GENERATION_TYPE.GENERATION}
			/>
			{/* ================ PSE MERGE START PART ================ */}
			<DateFormDialog
				title="Veuillez sélectionner le jour de production pour lequel vous souhaitez fusionner des étapes de production."
				onClose={handleClosePSEFusionDialog}
				open={openPSEFusionDialog && !openPSEFusionStep2Dialog}
				onConfirm={togglePSEFusionDialog}
				buttonText="Suivant"
				inputLabel="Jour de production"
				defaultDate={selectedDate}
			/>

			<PSEFusionStep2Dialog
				title="Veuillez sélectionner les étapes que vous souhaitez fusionner."
				onClose={handleClosePSEFusionDialog}
				open={openPSEFusionDialog && openPSEFusionStep2Dialog}
				onConfirm={handleMergeReusableSteps}
				buttonText="Valider"
				inputLabel="Fusion de recettes"
				selectedDate={selectedDate}
				reusableStepList={reusableStepOptions}
			/>
			{/* ================ PSE MERGE END PART ================ */}
		</div>
	)
}
export default ProductionStepsFilter

